import { useState } from "react";

const FORM_URL = "https://readdy.ai/api/form/d6u32ga9puk3bj9672ng";

type FormStatus = "idle" | "submitting" | "success" | "error";

export default function ServicesContact() {
  const [formStatus, setFormStatus] = useState<FormStatus>("idle");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setFormStatus("submitting");
    const data = new URLSearchParams();
    const elements = form.elements;
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i] as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      if (el.name && el.value) {
        data.append(el.name, el.value);
      }
    }

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setFormStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setFormStatus("error");
      }
    } catch {
      setFormStatus("error");
    }
  };

  return (
    <section id="quote-form" className="py-24 bg-[#F8FAFE]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left */}
          <div>
            <span className="text-[#1E88E5] font-heading font-semibold text-sm uppercase tracking-widest block mb-3">
              Free Quote
            </span>
            <h2
              className="font-heading font-800 text-[#0F2040] leading-tight mb-5"
              style={{ fontSize: "clamp(1.8rem, 3vw, 2.5rem)", fontWeight: 800 }}
            >
              Get Your Free Window Cleaning Quote
            </h2>
            <p className="text-gray-600 font-body text-base leading-relaxed mb-8">
              Ready to book or just want to know the price? Fill in the form and our team will get back to you quickly with a clear, no-obligation quote for your property in <strong>Northfleet, Gravesend, Kent and surrounding areas</strong>.
            </p>

            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-[#EBF4FF] rounded-xl flex-shrink-0">
                  <i className="ri-phone-fill text-[#1E88E5] text-lg"></i>
                </div>
                <div>
                  <span className="font-heading font-700 text-[#0F2040] text-sm block mb-0.5" style={{ fontWeight: 700 }}>Prefer to call?</span>
                  <a href="tel:02084322244" className="text-[#1E88E5] font-body text-lg font-semibold hover:text-[#1565C0] transition-colors cursor-pointer">
                    0208 43 222 44
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-[#EBF4FF] rounded-xl flex-shrink-0">
                  <i className="ri-time-fill text-[#1E88E5] text-lg"></i>
                </div>
                <div>
                  <span className="font-heading font-700 text-[#0F2040] text-sm block mb-0.5" style={{ fontWeight: 700 }}>Opening Hours</span>
                  <span className="text-gray-500 font-body text-sm">Mon – Fri: 9:00 AM – 5:00 PM</span>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-[#EBF4FF] rounded-xl flex-shrink-0">
                  <i className="ri-check-double-fill text-[#1E88E5] text-lg"></i>
                </div>
                <div>
                  <span className="font-heading font-700 text-[#0F2040] text-sm block mb-0.5" style={{ fontWeight: 700 }}>We Respond Within 24 Hours</span>
                  <span className="text-gray-500 font-body text-sm">No obligation · Transparent pricing · Fully insured team</span>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="bg-white rounded-2xl p-8 border border-gray-100">
            {formStatus === "success" ? (
              <div className="flex flex-col items-center justify-center text-center py-10">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mb-4">
                  <i className="ri-check-line text-green-500 text-2xl"></i>
                </div>
                <h3 className="font-heading font-700 text-[#0F2040] text-xl mb-2" style={{ fontWeight: 700 }}>Quote Request Sent!</h3>
                <p className="text-gray-500 font-body text-sm max-w-xs">
                  We&apos;ll get back to you within 24 hours with your free quote.
                </p>
                <button
                  onClick={() => setFormStatus("idle")}
                  className="mt-6 bg-[#1E88E5] text-white font-heading font-semibold text-sm px-6 py-3 rounded-lg cursor-pointer whitespace-nowrap hover:bg-[#1565C0] transition-colors"
                >
                  Submit Another
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                id="quote-form-services"
                className="flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block font-body text-sm font-medium text-gray-700 mb-1.5">Full Name *</label>
                    <input
                      name="name"
                      type="text"
                      required
                      placeholder="John Smith"
                      className="w-full bg-[#F8FAFE] border border-gray-200 rounded-lg px-4 py-3 text-sm font-body text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1E88E5] focus:ring-2 focus:ring-[#1E88E5]/20 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-sm font-medium text-gray-700 mb-1.5">Phone Number *</label>
                    <input
                      name="phone"
                      type="tel"
                      required
                      placeholder="07700 900 123"
                      className="w-full bg-[#F8FAFE] border border-gray-200 rounded-lg px-4 py-3 text-sm font-body text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1E88E5] focus:ring-2 focus:ring-[#1E88E5]/20 transition-all"
                    />
                  </div>
                </div>
                <div>
                  <label className="block font-body text-sm font-medium text-gray-700 mb-1.5">Email Address *</label>
                  <input
                    name="email"
                    type="email"
                    required
                    placeholder="john@example.com"
                    className="w-full bg-[#F8FAFE] border border-gray-200 rounded-lg px-4 py-3 text-sm font-body text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1E88E5] focus:ring-2 focus:ring-[#1E88E5]/20 transition-all"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block font-body text-sm font-medium text-gray-700 mb-1.5">Service Required</label>
                    <select
                      name="service"
                      className="w-full bg-[#F8FAFE] border border-gray-200 rounded-lg px-4 py-3 text-sm font-body text-gray-800 focus:outline-none focus:border-[#1E88E5] focus:ring-2 focus:ring-[#1E88E5]/20 transition-all cursor-pointer"
                    >
                      <option value="">Select a service...</option>
                      <option>Residential Window Cleaning</option>
                      <option>Commercial Window Cleaning</option>
                      <option>Conservatory / Glass Cleaning</option>
                      <option>Regular Maintenance Cleaning</option>
                      <option>Residential Gutter Clearance (One-Off)</option>
                      <option>Residential Gutter Clearance (Regular Maintenance)</option>
                      <option>Commercial Gutter Clearance (One-Off)</option>
                      <option>Commercial Gutter Clearance (Contract)</option>
                      <option>Other / Not Sure</option>
                    </select>
                  </div>
                  <div>
                    <label className="block font-body text-sm font-medium text-gray-700 mb-1.5">Property Type</label>
                    <select
                      name="property_type"
                      className="w-full bg-[#F8FAFE] border border-gray-200 rounded-lg px-4 py-3 text-sm font-body text-gray-800 focus:outline-none focus:border-[#1E88E5] focus:ring-2 focus:ring-[#1E88E5]/20 transition-all cursor-pointer"
                    >
                      <option value="">Select type...</option>
                      <option>Flat / Apartment</option>
                      <option>Terraced House</option>
                      <option>Semi-Detached</option>
                      <option>Detached House</option>
                      <option>Office / Commercial</option>
                      <option>Retail Unit</option>
                      <option>Other</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block font-body text-sm font-medium text-gray-700 mb-1.5">
                    Additional Details
                    <span className={`ml-2 text-xs ${charCount > 450 ? "text-red-500" : "text-gray-400"}`}>
                      {charCount}/500
                    </span>
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    placeholder="Tell us about your property, number of windows, frequency required..."
                    className="w-full bg-[#F8FAFE] border border-gray-200 rounded-lg px-4 py-3 text-sm font-body text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1E88E5] focus:ring-2 focus:ring-[#1E88E5]/20 transition-all resize-none"
                  ></textarea>
                </div>
                {formStatus === "error" && (
                  <p className="text-red-500 text-sm font-body">
                    Something went wrong. Please try again or call us directly.
                  </p>
                )}
                <button
                  type="submit"
                  disabled={formStatus === "submitting"}
                  className="w-full flex items-center justify-center gap-2 bg-[#1E88E5] hover:bg-[#1565C0] disabled:opacity-60 text-white font-heading font-semibold text-base py-4 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  {formStatus === "submitting" ? (
                    <><i className="ri-loader-4-line animate-spin"></i> Sending...</>
                  ) : (
                    <><i className="ri-send-plane-fill"></i> Request Free Quote</>
                  )}
                </button>
                <p className="text-center text-gray-400 text-xs font-body flex items-center justify-center gap-1">
                  <i className="ri-lock-line text-gray-300"></i>
                  Your details are safe · No spam · No obligation
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
